import React from "react";
import { cn } from "@/lib/utils";

/**
 * Radio — controlled/uncontrolled `<input type="radio">` primitive.
 *
 * Consumers manage `name` grouping. Same layout convention as
 * Checkbox — label row with description slot.
 */

export const Radio = React.forwardRef(function Radio(
  {
    label,
    description,
    className,
    invalid = false,
    disabled = false,
    id,
    "data-testid": testId,
    ...rest
  },
  ref
) {
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const descId = description ? `${inputId}-desc` : undefined;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        "inline-flex items-start gap-3 text-[14px]",
        "text-[var(--ds-color-ink)]",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        className
      )}
    >
      <input
        ref={ref}
        id={inputId}
        type="radio"
        disabled={disabled}
        // eslint-plugin-jsx-a11y flags aria-invalid as "unsupported by role
        // radio", but aria-invalid IS valid on <input> per WAI-ARIA. Keep
        // the attribute — tests assert on it — and rely on the a11y-lint
        // pass to flag as a WARNING only.
        aria-invalid={invalid || undefined}
        aria-describedby={descId}
        data-testid={testId}
        className={cn(
          "mt-[2px] h-4 w-4 shrink-0",
          "border border-[var(--ds-color-border-strong)]",
          "accent-[var(--ds-color-accent)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ds-color-background)]",
          invalid && "border-[var(--ds-color-danger)]"
        )}
        {...rest}
      />
      {(label || description) && (
        <span className="flex flex-col leading-snug">
          {label && <span>{label}</span>}
          {description && (
            <span
              id={descId}
              className="text-[13px] text-[var(--ds-color-ink-muted)]"
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  );
});

Radio.displayName = "DSRadio";

export default Radio;
